import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ArrowRight, Check } from "lucide-react";

import { RootState } from "../../../../redux/store";
import logoImg from "../../../../assets/app/logo.png";

/* ═══════════════════════════════════════════════════════════
   PALETTE — same HEART tokens HomeCentral uses
═══════════════════════════════════════════════════════════ */
const HEART = {
  bg: "#FBF6EE",
  dark: "#1B0E06",
  textMuted: "#7A6653",
  red: "#C8202E",
  redDark: "#9E1521",
  gold: "#F2A93E",
  goldDark: "#D4900F",
  goldSoft: "#FCEFD4",
  border: "#EEDFC4",
  divider: "#F3E6CE",
  green: "#3F8A4C",
  greenDark: "#2E6A38",
  greenSoft: "#E7F3E8",
};

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800;900&family=DM+Sans:wght@400;500;600;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  @keyframes ckShimmer { 0%{background-position:0% 50%;} 100%{background-position:200% 50%;} }
  @keyframes ckWiggle { 0%,100%{transform:rotate(-2deg);} 50%{transform:rotate(2deg);} }
  @keyframes ckPulse { 0%{transform:scale(1);opacity:.5;} 70%{transform:scale(1.7);opacity:0;} 100%{transform:scale(1.7);opacity:0;} }

  .ck-scope { font-family: 'DM Sans', sans-serif; background: ${HEART.bg}; color: ${HEART.dark}; }

  /* 3D extruded type — layered text-shadow through the brand ramp */
  .ck-3d {
    font-family: 'Sora', sans-serif; font-weight: 900; letter-spacing: -0.03em;
    color: ${HEART.dark};
    text-shadow:
      0 1px 0 #fff,
      0 2px 0 ${HEART.divider},
      0 3px 0 ${HEART.border},
      0 4px 0 ${HEART.goldSoft},
      0 5px 0 ${HEART.gold},
      0 6px 0 ${HEART.goldDark},
      0 8px 14px rgba(27,14,6,0.26);
  }
  .ck-3d-green {
    font-family: 'Sora', sans-serif; font-weight: 900; letter-spacing: -0.03em;
    color: ${HEART.green};
    text-shadow:
      0 1px 0 #fff,
      0 2px 0 ${HEART.border},
      0 3px 0 ${HEART.greenSoft},
      0 4px 0 #A9D2AF,
      0 5px 0 ${HEART.greenDark},
      0 9px 16px rgba(63,138,76,0.30);
  }

  .ck-cta {
    display: inline-flex; align-items: center; gap: 8px;
    background: ${HEART.red}; color: #fff;
    border: 3px solid ${HEART.dark}; border-radius: 14px;
    padding: 15px 30px;
    font-family: 'Sora', sans-serif; font-size: 15px; font-weight: 800;
    cursor: pointer; transition: background .2s, transform .15s; white-space: nowrap;
  }
  .ck-cta:hover { background: ${HEART.redDark}; transform: translateY(-2px); }

  .ck-logo-wiggle { animation: ckWiggle 2.6s ease-in-out infinite; transform-origin: center; }

  @media (max-width: 768px) {
    .ck-logo  { width: 150px !important; height: 150px !important; }
    .ck-head  { font-size: 40px !important; }
    .ck-thanks{ font-size: 17px !important; }
  }

  @media (prefers-reduced-motion: reduce) { .ck-scope * { animation: none !important; } }
`;

/* ═══ WATERMARK — tiled logo + wordmark ═══ */
const Watermark: React.FC = () => (
  <div style={{
    position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden",
    display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gridTemplateRows: "repeat(6, 1fr)",
  }}>
    {Array.from({ length: 36 }).map((_, i) => (
      <div key={i} style={{
        display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
        transform: i % 2 === 0 ? "rotate(-18deg)" : "rotate(-18deg) translateY(12px)",
        opacity: 0.05,
      }}>
        <img src={logoImg} alt="" style={{ width: 22, height: 22, borderRadius: 5, objectFit: "cover" }} />
        <span style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 15, fontStyle: "italic", color: HEART.dark, whiteSpace: "nowrap" }}>
          Heartlas
        </span>
      </div>
    ))}
  </div>
);

/* ═══ ANIMATED SUCCESS CHECK — ring draws in, tick pops, halo pulses ═══ */
const SuccessCheck: React.FC = () => (
  <div style={{ position: "relative", width: 108, height: 108, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 26 }}>
    {/* pulsing halo */}
    <span style={{
      position: "absolute", inset: 0, borderRadius: "50%", background: HEART.green,
      animation: "ckPulse 2s ease-out infinite",
    }} />

    {/* drawn ring */}
    <svg width={108} height={108} viewBox="0 0 108 108" style={{ position: "absolute", inset: 0, transform: "rotate(-90deg)" }}>
      <motion.circle
        cx={54} cy={54} r={49}
        fill="none" stroke={HEART.green} strokeWidth={5} strokeLinecap="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 0.7, ease: "easeInOut" }}
      />
    </svg>

    {/* filled disc + tick */}
    <motion.div
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: "spring", stiffness: 320, damping: 16, delay: 0.55 }}
      style={{
        width: 78, height: 78, borderRadius: "50%", background: HEART.green,
        display: "flex", alignItems: "center", justifyContent: "center",
        boxShadow: "0 10px 26px rgba(63,138,76,0.42)", position: "relative",
      }}
    >
      <motion.span
        initial={{ scale: 0.4, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 400, damping: 14, delay: 0.72 }}
        style={{ display: "flex" }}
      >
        <Check size={42} color="#fff" strokeWidth={3.4} />
      </motion.span>
    </motion.div>
  </div>
);

/* ═══════════════════════════════════════════════════════════
   CHECKOUT SUCCESS
═══════════════════════════════════════════════════════════ */
const CheckoutSuccess: React.FC = () => {
  const navigate = useNavigate();
  const user = useSelector((s: RootState) => s.auth.user);
  const firstName = user?.firstName || "there";
  const userId = user?.userId ?? "";

  return (
    <div className="ck-scope" style={{
      position: "relative", minHeight: "100vh", width: "100%", overflow: "hidden",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      textAlign: "center", padding: "60px 24px",
    }}>
      <style>{CSS}</style>

      <Watermark />

      {/* warm shimmer wash */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: "linear-gradient(100deg, transparent 20%, rgba(63,138,76,0.08) 35%, rgba(242,169,62,0.11) 50%, rgba(212,144,15,0.09) 65%, transparent 80%)",
        backgroundSize: "300% 300%", animation: "ckShimmer 3.6s ease-in-out infinite",
      }} />

      <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center", maxWidth: 820 }}>
        {/* huge logo */}
        <motion.img
          src={logoImg}
          alt="Heartlas"
          className="ck-logo ck-logo-wiggle"
          initial={{ opacity: 0, scale: 0.86, y: 18 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          style={{
            width: 220, height: 220, borderRadius: 48, objectFit: "cover",
            marginBottom: 34, boxShadow: "0 22px 60px rgba(27,14,6,0.22)",
          }}
        />

        {/* thanks + name */}
        <motion.p
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }}
          className="ck-thanks"
          style={{ fontSize: 20, fontWeight: 700, color: HEART.textMuted, marginBottom: 12 }}
        >
          Thanks for shopping, <span style={{ color: HEART.red, fontWeight: 800 }}>{firstName}</span>
        </motion.p>

        {/* order confirmed */}
        <motion.h1
          initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.25 }}
          className="ck-3d ck-head"
          style={{ fontSize: 60, lineHeight: 1.05, marginBottom: 34 }}
        >
          Order Confirmed
        </motion.h1>

        {/* animated green success check */}
        <SuccessCheck />

        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 1 }}
          className="ck-3d-green"
          style={{ fontSize: 24, marginBottom: 34 }}
        >
          Success
        </motion.p>

        {/* CTA → buyer orders */}
        <motion.button
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, delay: 1.15 }}
          className="ck-cta"
          onClick={() => navigate(`/buyerOrders/${userId}`)}
        >
          View My Orders <ArrowRight size={15} />
        </motion.button>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
